import { ReactNode, useEffect } from "react";
import { getIsAuthenticated, userDomains, userSelectedHq } from "atoms/auth";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { AdfenixQuery } from "hooks";
import { GenericError } from "components/GenericError";
import type { Language } from "types/Language";
import { Modal } from "components/Modal";
import { ModalSelectHq } from "components/ModalSelectHq";
import Sentry from "lib/sentry/init";
import { Spinner } from "@adfenix/alexandria";
import dayjs from "dayjs";
import i18n from "lib/i18n/init";
import { languageMap } from "utils/language";
import queryString from "query-string";
import { selectHqModalState } from "atoms/selectedHqHoc";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import useUserInfo from "hooks/useUserInfo";
import { useHqAndDomains } from "api/services/configService";
import { getUser } from "api/services/authService";
import { getHqId } from "../utils/getHqId";

export const HandleSelectedHqHOC = ({ children }: { children: ReactNode }) => {
  const [modalState, setModalState] = useRecoilState(selectHqModalState);

  const isAuthenticated = useRecoilValue(getIsAuthenticated);
  const { userEmail, masqueradeUserEmail } = useUserInfo();
  const location = useLocation();
  const hqId = getHqId(location.pathname);

  const { data: userInfo, status: userInfoStatus } = useQuery(
    ["getUser", userEmail, masqueradeUserEmail],
    () => getUser(masqueradeUserEmail ? masqueradeUserEmail : userEmail),
    { enabled: Boolean(userEmail) }
  );
  const setDomains = useSetRecoilState(userDomains);

  const [selectedHq, setSelectedHq] = useRecoilState(userSelectedHq);
  const { data: hqs, status } = useHqAndDomains(userEmail);

  if (status === "error") {
    Sentry.captureException("Could not load hqs");
  }

  useEffect(() => {
    if (!userInfo) return;

    setDomains(userInfo.domains ?? []);

    if (location.pathname.startsWith("/back-office") && !selectedHq) {
      setSelectedHq(hqs?.[0]);
      setModalState(false);
      return;
    }

    //TODO: Should be able to filter on userInfo.hqs instead, or?
    const availableHqs = hqs?.filter(hq =>
      userInfo.domains?.some(domain => domain === hq.domain)
    );

    if (hqId && hqs) {
      const hqMatchingId = hqs.find(hq => hq.id === hqId);

      if (hqMatchingId) {
        setSelectedHq(hqMatchingId);
        setModalState(false);
      }
    }

    const activeHqs = availableHqs?.filter(hq => hq.active);

    if (availableHqs?.length === 1) {
      setSelectedHq(availableHqs[0]);
      setModalState(false);
    } else if (activeHqs?.length === 1) {
      setSelectedHq(activeHqs[0]);
      setModalState(false);
    } else if (!selectedHq) {
      setModalState(true);
    }
  }, [
    hqs,
    location,
    setDomains,
    setModalState,
    setSelectedHq,
    userInfo,
    selectedHq,
    hqId
  ]);

  useEffect(() => {
    const query = queryString.parse(global.location.search) as AdfenixQuery;
    const queryStringLanguage = query["language"] as Language;

    if (queryStringLanguage && queryStringLanguage !== i18n.language) {
      i18n.changeLanguage(queryStringLanguage);
      dayjs.locale(languageMap[queryStringLanguage] ?? "en");
    } else if (selectedHq?.language && selectedHq.language !== i18n.language) {
      i18n.changeLanguage(selectedHq.language ?? "en_GB");
      dayjs.locale(
        languageMap[(selectedHq.language as Language) ?? "en_GB"] ?? "en"
      );
    }
  }, [selectedHq]);

  if (!isAuthenticated) return <>{children}</>;

  if (status === "loading" || userInfoStatus === "loading")
    return <Spinner centered />;

  if (status === "error" || userInfoStatus === "error") return <GenericError />;

  if (!selectedHq) {
    return (
      <Modal show={modalState} onClose={() => setModalState(false)}>
        <ModalSelectHq hqs={hqs} />
      </Modal>
    );
  }

  return (
    <>
      <Modal show={modalState} onClose={() => setModalState(false)}>
        <ModalSelectHq hqs={hqs} />
      </Modal>

      {children}
    </>
  );
};
